export type CfdPushPlatform = 'android' | 'ios' | 'web'

export interface CfdPushResult {
  ok: boolean
  httpCode: number
  data: unknown
}

export interface CfdPushState {
  platform: Exclude<CfdPushPlatform, 'web'>
  device_id: string
  fcm_token: string | null
  has_session: boolean
  push_enabled: boolean
  permission_granted: boolean
  app_version: string
}

export interface CfdPushMessage {
  title?: string
  body?: string
  data?: Record<string, unknown> | null
  received_at?: string
  [key: string]: unknown
}

export type CfdPushMessageHandler = (message: CfdPushMessage) => void
export type CfdPushMessageUnsubscribe = () => void

export interface CfdPushBridge {
  platform: () => CfdPushPlatform
  isApp: () => boolean
  isAndroid: () => boolean
  isIOS: () => boolean
  setSession: (accessToken: string) => Promise<CfdPushResult>
  clearSession: () => Promise<CfdPushResult>
  setEnabled: (enabled: boolean) => Promise<CfdPushResult>
  register: () => Promise<CfdPushResult>
  state: () => Promise<CfdPushState | null>
  requestPermission: () => void
  onPushMessage: (handler: CfdPushMessageHandler) => CfdPushMessageUnsubscribe
}

interface CfdAppBridge {
  getPlatform?: () => string
  getPushState?: () => string
  setSession?: (sessionId: string, callbackId: string) => void
  clearSession?: (callbackId: string) => void
  setPushEnabled?: (enabled: boolean, callbackId: string) => void
  registerPush?: (callbackId: string) => void
  requestNotificationPermission?: () => void
}

interface CfdIosMessage {
  method: string
  args?: Record<string, unknown>
  callbackId?: string
}

interface CfdWebkitBridge {
  messageHandlers?: {
    CfdApp?: {
      postMessage: (message: CfdIosMessage) => void
    }
  }
}

declare global {
  interface Window {
    CfdApp?: CfdAppBridge
    CfdAppCallback?: (callbackId: string, result: CfdPushResult) => void
    CfdPushMessageCallback?: (message: CfdPushMessage | string) => void
    CfdPush?: CfdPushBridge
    webkit?: CfdWebkitBridge
  }
}

const TIMEOUT_MS = 15_000
const NOT_APP_RESULT: CfdPushResult = {
  ok: false,
  httpCode: 0,
  data: { message: 'not app' },
}

export const getCfdPushBridge = (
  windowRef: Window | undefined = typeof window === 'undefined' ? undefined : window
): CfdPushBridge | undefined => windowRef?.CfdPush

export const getCfdPushPlatform = (
  windowRef: Window | undefined = typeof window === 'undefined' ? undefined : window
): CfdPushPlatform => {
  if (!windowRef) return 'web'
  if (windowRef.CfdApp && typeof windowRef.CfdApp.getPlatform === 'function') return 'android'
  if (windowRef.webkit?.messageHandlers?.CfdApp) return 'ios'
  return 'web'
}

export const isCfdPushAppWebView = (
  windowRef: Window | undefined = typeof window === 'undefined' ? undefined : window
): boolean => getCfdPushPlatform(windowRef) !== 'web'

let seq = 0
let installedWindow: Window | undefined
const waiting: Record<
  string,
  {
    resolve: (result: CfdPushResult) => void
    timer: ReturnType<typeof setTimeout>
  }
> = {}
const pushMessageListeners = new Set<CfdPushMessageHandler>()

const resolveWaiting = (callbackId: string, result: CfdPushResult) => {
  const entry = waiting[callbackId]
  if (!entry) return
  delete waiting[callbackId]
  clearTimeout(entry.timer)
  entry.resolve(result)
}

const normalizePushMessage = (message: CfdPushMessage | string): CfdPushMessage => {
  if (typeof message !== 'string') return message

  try {
    const parsed = JSON.parse(message)
    return parsed && typeof parsed === 'object' ? parsed : { body: message }
  } catch {
    return { body: message }
  }
}

export const emitCfdPushMessage = (message: CfdPushMessage | string) => {
  const normalized = normalizePushMessage(message)
  pushMessageListeners.forEach(listener => listener(normalized))
}

export const addCfdPushMessageListener = (
  handler: CfdPushMessageHandler
): CfdPushMessageUnsubscribe => {
  pushMessageListeners.add(handler)
  return () => {
    pushMessageListeners.delete(handler)
  }
}

const callAndroid = (
  windowRef: Window,
  method: string,
  args: Record<string, unknown>,
  callbackId: string
) => {
  switch (method) {
    case 'setSession':
      windowRef.CfdApp?.setSession?.(String(args.sessionId || ''), callbackId)
      break
    case 'clearSession':
      windowRef.CfdApp?.clearSession?.(callbackId)
      break
    case 'setPushEnabled':
      windowRef.CfdApp?.setPushEnabled?.(!!args.enabled, callbackId)
      break
    case 'registerPush':
      windowRef.CfdApp?.registerPush?.(callbackId)
      break
    default:
      throw new Error(`unknown method: ${method}`)
  }
}

const send = (
  windowRef: Window,
  method: string,
  args: Record<string, unknown> = {}
): Promise<CfdPushResult> => {
  const platform = getCfdPushPlatform(windowRef)
  if (platform === 'web') return Promise.resolve(NOT_APP_RESULT)

  return new Promise(resolve => {
    const callbackId = `cb_${++seq}`
    const timer = setTimeout(() => {
      if (!waiting[callbackId]) return
      delete waiting[callbackId]
      resolve({ ok: false, httpCode: 0, data: { message: 'timeout' } })
    }, TIMEOUT_MS)

    waiting[callbackId] = { resolve, timer }

    try {
      if (platform === 'android') {
        callAndroid(windowRef, method, args, callbackId)
      } else {
        windowRef.webkit?.messageHandlers?.CfdApp?.postMessage({
          method,
          args,
          callbackId,
        })
      }
    } catch (error) {
      delete waiting[callbackId]
      clearTimeout(timer)
      resolve({ ok: false, httpCode: 0, data: { message: String(error) } })
    }
  })
}

const getState = (windowRef: Window): Promise<CfdPushState | null> => {
  const platform = getCfdPushPlatform(windowRef)

  if (platform === 'android') {
    try {
      const state = windowRef.CfdApp?.getPushState?.()
      return Promise.resolve(state ? JSON.parse(state) : null)
    } catch {
      return Promise.resolve(null)
    }
  }

  if (platform === 'ios') {
    return send(windowRef, 'getPushState').then(result =>
      result.ok ? (result.data as CfdPushState) : null
    )
  }

  return Promise.resolve(null)
}

export const installCfdPushBridge = (
  windowRef: Window | undefined = typeof window === 'undefined' ? undefined : window
): CfdPushBridge | undefined => {
  if (!windowRef) return undefined
  if (windowRef.CfdPush) return windowRef.CfdPush

  windowRef.CfdAppCallback = resolveWaiting
  windowRef.CfdPushMessageCallback = emitCfdPushMessage

  windowRef.CfdPush = {
    platform: () => getCfdPushPlatform(windowRef),
    isApp: () => getCfdPushPlatform(windowRef) !== 'web',
    isAndroid: () => getCfdPushPlatform(windowRef) === 'android',
    isIOS: () => getCfdPushPlatform(windowRef) === 'ios',
    setSession: accessToken => send(windowRef, 'setSession', { sessionId: accessToken }),
    clearSession: () => send(windowRef, 'clearSession'),
    setEnabled: enabled => send(windowRef, 'setPushEnabled', { enabled: !!enabled }),
    register: () => send(windowRef, 'registerPush'),
    state: () => getState(windowRef),
    requestPermission: () => {
      const platform = getCfdPushPlatform(windowRef)
      if (platform === 'android') {
        windowRef.CfdApp?.requestNotificationPermission?.()
      } else if (platform === 'ios') {
        send(windowRef, 'requestNotificationPermission')
      }
    },
    onPushMessage: addCfdPushMessageListener,
  }

  installedWindow = windowRef
  return windowRef.CfdPush
}

export const getInstalledCfdPushBridge = (): CfdPushBridge | undefined =>
  installCfdPushBridge(installedWindow)
